import { BrandLogoIcon, Button, CheckIcon, FullPageCard, Select, Spinner } from '@st/theme'
import { humanJoin } from '@st/util/inflect'
import { match, P } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { OrganizationLoginPage } from '../organizations'
import { OrganizationSignUpScreen } from '../organizations/pages/organization-sign-up-page'
import { Link } from '../link'
import type {
  ConnectApp,
  ConnectAppMessage,
  AuthenticatedConnectAppState,
  ConnectAppModuleState
} from './connect-app-module'

export function ConnectAppPage() {
  const connect = useProcess('connectApp')
  const connectState = useSelect('connectApp', (s) => s)

  return match(connectState)
    .with({ status: P.union('waitingForAuth', 'finishing') }, () => <></>)
    .with({ status: P.union('loggedOut', 'signingUp') }, (state) =>
      renderAuthPage(state, connect.send)
    )
    .with(
      { status: P.union('routeUnavailable', 'loadingMemberships', 'membershipsFailed', 'ready') },
      (state) => <AuthenticatedConnectAppPage state={state} send={connect.send} />
    )
    .exhaustive()
}

function renderAuthPage(
  state: Extract<ConnectAppModuleState, { status: 'loggedOut' | 'signingUp' }>,
  send: (message: ConnectAppMessage) => void
) {
  return match(state.authPage)
    .with('login', () => (
      <OrganizationLoginPage
        header={<h1 className="w-max-500 text-center text-3xl">{`You're almost in!`}</h1>}
        onClickSignUp={() => send({ type: 'showSignUp' })}
      />
    ))
    .with('signUp', () => (
      <OrganizationSignUpScreen
        state={state.signUp}
        send={(message) => send({ type: 'signUp', message })}
        onClickLogin={() => send({ type: 'showLogin' })}
      />
    ))
    .exhaustive()
}

function AuthenticatedConnectAppPage({
  state,
  send
}: {
  state: AuthenticatedConnectAppState
  send: (message: ConnectAppMessage) => void
}) {
  if (state.status == 'routeUnavailable') {
    if (state.route.status == 'inactive') {
      return null
    }
    const subtitle = match(state.route.reason)
      .with('unknown_app', () => `Unknown connected app. Please re-run the integration flow.`)
      .with(
        'missing_params',
        () =>
          `This connect link is missing required parameters. Please re-run the integration flow.`
      )
      .exhaustive()

    return (
      <FullPageCard>
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="mt-4 text-lg font-semibold text-gray-900">Invalid connect link</p>
          <p className="text-sm text-gray-600">{subtitle}</p>
          {state.route.clientId && (
            <p className="text-xs text-gray-500">{`client_id: ${state.route.clientId}`}</p>
          )}
        </div>
      </FullPageCard>
    )
  }

  if (state.status == 'loadingMemberships') {
    return <ConnectLoadingPage />
  }

  if (state.status == 'membershipsFailed') {
    return (
      <FullPageCard>
        <div className="flex h-64 flex-col items-center justify-center gap-4">
          <p className="text-sm text-red-500">Failed to load organizations.</p>
          <Button variant="default" onClick={() => send({ type: 'reloadMemberships' })}>
            Retry
          </Button>
        </div>
      </FullPageCard>
    )
  }

  const app = state.route.app
  const title = `${app.name} wants to access StanfordTax`
  const memberships = state.memberships
  const selectedMembership = memberships.find(
    (m) => m.organization.id == state.selectedOrganizationId
  )

  const connectNotAllowed =
    selectedMembership && !app.allowedOrganizationRoles.includes(selectedMembership.role)

  const isNotMemberOfAnyOrganization = memberships.length == 0
  const isAuthorizing = state.authorization.status == 'authorizing'

  if (isAuthorizing) {
    return <ConnectLoadingPage />
  }

  return (
    <FullPageCard>
      <div className="flex flex-col items-center gap-3">
        <ConnectHeader app={app} title={title} />

        <Select
          className="mt-4 w-full border-2 border-blue-700"
          value={state.selectedOrganizationId ?? undefined}
          placeholder="Select an organization"
          options={memberships}
          onChange={(orgId) => {
            if (!orgId) return
            send({ type: 'selectOrganization', organizationId: orgId })
          }}
          buildValue={(m) => m?.organization.id}
          buildLabel={(m) => m?.organization.name}
        />

        <ul className="my-6 w-full space-y-4 px-4">
          {app.permissions.map((permission) => (
            <li key={permission} className="flex gap-x-3 text-base">
              <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
              {permission}
            </li>
          ))}
        </ul>

        <Button
          size="m"
          className="mt-4 w-full"
          variant="primary"
          onClick={() => send({ type: 'authorizeClicked' })}
          disabled={
            !state.selectedOrganizationId || connectNotAllowed || isNotMemberOfAnyOrganization
          }
        >
          Authorize
        </Button>

        {state.authorization.status == 'failed' && (
          <p className="mt-4 text-center text-sm text-red-500">
            Failed to authorize. Please try again.
          </p>
        )}

        {connectNotAllowed && (
          <p className="mt-4 text-center text-sm text-red-500">
            You are not authorized to connect this app because you must have the{' '}
            {humanJoin(app.allowedOrganizationRoles, 'or')} role.
          </p>
        )}

        {isNotMemberOfAnyOrganization && (
          <>
            <p className="mt-4 text-center text-sm text-red-500">
              You are not a member of any StanfordTax organizations. Please create a StanfordTax
              organization or get invited to one. Then try connecting StanfordTax again.{' '}
            </p>

            <Link className="text-sm text-blue-500" href={{ name: 'sign_up' }}>
              Click here to setup a StanfordTax organization
            </Link>
          </>
        )}
      </div>
    </FullPageCard>
  )
}

function ConnectLoadingPage() {
  return (
    <FullPageCard>
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <Spinner className="size-12 fill-blue-500 text-blue-100" />
        <span>Loading</span>
      </div>
    </FullPageCard>
  )
}

export function ConnectHeader({ app, title }: { app: ConnectApp; title: string }) {
  return (
    <div className="flex flex-col items-center gap-3">
      {app.logo && (
        <div className="flex items-center gap-2">
          <img src={app.logo} className="size-12 rounded-md" />
          <ConnectArrow />
          <BrandLogoIcon />
        </div>
      )}
      <p className="mt-4 text-center">{title}</p>
    </div>
  )
}

function ConnectArrow() {
  return (
    <svg viewBox="0 0 24 24" className="size-6">
      <path
        fillRule="evenodd"
        d="M15.97 2.47a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 1 1-1.06-1.06l3.22-3.22H7.5a.75.75 0 0 1 0-1.5h11.69l-3.22-3.22a.75.75 0 0 1 0-1.06Zm-7.94 9a.75.75 0 0 1 0 1.06l-3.22 3.22H16.5a.75.75 0 0 1 0 1.5H4.81l3.22 3.22a.75.75 0 1 1-1.06 1.06l-4.5-4.5a.75.75 0 0 1 0-1.06l4.5-4.5a.75.75 0 0 1 1.06 0Z"
        clipRule="evenodd"
      />
    </svg>
  )
}
